import type { FundPolicy } from '../../../core/fundPolicy/schema';
import { dependentRelationSchema } from '../../../core/fundPolicy/schema';
import {
  genderSchema,
  isoDateSchema,
  nationalIdSchema,
  personNameSchema,
} from '../../../core/primitives/schema';
import { ageInYearsOn, evaluateDependentEligibility } from '../domain/dependent/eligibility';
import { composeChildName } from './childName';
import type { MyDependent, NewDependent } from './ports/dependentPort';

/**
 * Validation for the add/edit dependant form.
 *
 * Pure, so the rules a person is held to can be tested without rendering
 * anything (§5.4). Eligibility is evaluated against the *stored* policy, never
 * against a hardcoded age or relationship.
 */

export interface DependentFormValues {
  readonly relation: string;
  /**
   * What the employee typed.
   *
   * For a son or daughter this is the first name alone and the rest is derived
   * from the employee's own name; for anyone else it is the full name. Which
   * of the two it holds is decided by `relation` and `differentFather`, so the
   * field never means two things at the same time.
   */
  readonly fullName: string;
  /** Half-siblings from the mother: the lineage cannot be derived. */
  readonly differentFather: boolean;
  readonly gender: string;
  readonly birthDate: string;
  readonly nationalId: string;
}

export const EMPTY_DEPENDENT_FORM: DependentFormValues = {
  relation: '',
  fullName: '',
  differentFather: false,
  gender: '',
  birthDate: '',
  nationalId: '',
};

/** Relations whose name is derived from the employee's own. */
const CHILD_RELATIONS = ['son', 'daughter'];

/**
 * Whether this entry needs only a first name.
 *
 * Exported because the screen decides which field to show from the same
 * answer the validator uses — two copies of this condition is how a form ends
 * up asking for one thing and rejecting another.
 */
export function needsOnlyFirstName(values: {
  readonly relation: string;
  readonly differentFather: boolean;
}): boolean {
  return CHILD_RELATIONS.includes(values.relation) && !values.differentFather;
}

export type DependentFieldErrors = Partial<Record<keyof DependentFormValues, string>>;

export interface DependentValidation {
  readonly errors: DependentFieldErrors;
  /** Reasons the fund's rules refuse this person, distinct from bad input. */
  readonly ineligibleReasonsAr: readonly string[];
  readonly requiresProofDocument: boolean;
  readonly valid: boolean;
}

/** A dependant born tomorrow is a typo; one born 120 years ago is too. */
const MAXIMUM_PLAUSIBLE_AGE = 120;

/**
 * Validates the form and evaluates eligibility together, reporting every
 * failure at once.
 *
 * Input errors and eligibility failures are kept separate on purpose: "the
 * date is malformed" is the employee's mistake to fix, while "children are
 * covered to 20" is the fund's rule and no amount of retyping will change it.
 * Presenting them the same way invites people to keep editing a correct entry.
 */
export function validateDependentForm(
  values: DependentFormValues,
  policy: FundPolicy,
  context: {
    readonly existingByRelation: Readonly<Partial<Record<string, number>>>;
    readonly existingTotal: number;
    readonly today: string;
    /** The employee's own recorded name, to derive a child's from. */
    readonly employeeFullName: string;
  },
): DependentValidation {
  const errors: DependentFieldErrors = {};

  const relation = dependentRelationSchema.safeParse(values.relation);
  if (!relation.success) errors.relation = 'اختر صلة القرابة';

  if (needsOnlyFirstName(values)) {
    if (composeChildName(values.fullName, context.employeeFullName) === null) {
      // Either the first name is empty, or the employee's own recorded name is
      // too short to derive from — the screen offers the full-name field then.
      errors.fullName = 'أدخل الاسم الأول';
    }
  } else if (!personNameSchema.safeParse(values.fullName).success) {
    errors.fullName = 'أدخل الاسم كاملًا';
  }

  if (!genderSchema.safeParse(values.gender).success) {
    errors.gender = 'اختر الجنس';
  }

  const birthDate = isoDateSchema.safeParse(values.birthDate);
  if (!birthDate.success) {
    errors.birthDate = birthDate.error.issues[0]?.message ?? 'تاريخ الميلاد غير صحيح';
  } else {
    const age = ageInYearsOn(birthDate.data, context.today);
    if (age < 0) {
      errors.birthDate = 'تاريخ الميلاد في المستقبل.';
    } else if (age > MAXIMUM_PLAUSIBLE_AGE) {
      errors.birthDate = 'تاريخ الميلاد غير منطقي. تحقق من السنة.';
    }
  }

  // Optional: a newborn may have no ID issued yet, and blocking enrolment on
  // paperwork the parent cannot obtain would be the wrong default.
  if (values.nationalId && !nationalIdSchema.safeParse(values.nationalId).success) {
    errors.nationalId = 'رقم الهوية يجب أن يتكون من أرقام فقط';
  }

  const inputValid = Object.keys(errors).length === 0;

  // Eligibility needs a valid relation and date, so it is only meaningful once
  // the input parses.
  if (!inputValid || !relation.success || !birthDate.success) {
    return { errors, ineligibleReasonsAr: [], requiresProofDocument: false, valid: false };
  }

  const eligibility = evaluateDependentEligibility(
    { relation: relation.data, birthDate: birthDate.data },
    policy,
    {
      existingByRelation: context.existingByRelation,
      existingTotal: context.existingTotal,
      asOf: context.today,
    },
  );

  return {
    errors,
    ineligibleReasonsAr: eligibility.failures.map((failure) => failure.messageAr),
    requiresProofDocument: eligibility.requiresProofDocument,
    valid: eligibility.eligible,
  };
}

export function toNewDependent(
  values: DependentFormValues,
  employeeFullName: string,
): NewDependent {
  // The stored name is always the full one. The short form is a convenience at
  // the keyboard, not a different kind of record — the committee reviewing a
  // child must see the same shape of name as for anyone else.
  const fullName = needsOnlyFirstName(values)
    ? (composeChildName(values.fullName, employeeFullName)?.fullName ?? values.fullName.trim())
    : values.fullName.trim();

  return {
    relation: dependentRelationSchema.parse(values.relation),
    fullName,
    gender: genderSchema.parse(values.gender),
    birthDate: isoDateSchema.parse(values.birthDate),
    ...(values.nationalId ? { nationalId: nationalIdSchema.parse(values.nationalId) } : {}),
  };
}

/** Counts by relation, for the eligibility caps. */
export function countByRelation(
  dependents: readonly MyDependent[],
): Readonly<Partial<Record<string, number>>> {
  const counts: Partial<Record<string, number>> = {};
  for (const dependent of dependents) {
    counts[dependent.relation] = (counts[dependent.relation] ?? 0) + 1;
  }
  return counts;
}

/**
 * How many dependants the roster suggested versus how many were entered.
 *
 * The imported children count is a cross-check, not a fact — but a mismatch is
 * worth surfacing to both the employee and the committee, because "I thought I
 * finished" is the most common way this ends up incomplete (spec FR-015).
 */
export function childrenShortfall(
  dependents: readonly MyDependent[],
  declaredChildrenCount: number,
): number {
  const entered = dependents.filter(
    (dependent) => dependent.relation === 'son' || dependent.relation === 'daughter',
  ).length;

  return Math.max(0, declaredChildrenCount - entered);
}
